/** @jsx jsx */
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { DayModifiers } from 'react-day-picker';
import { jsx } from '@balance-web/core';
import { useInputStyles } from '@balance-web/text-input';
import { Box } from '@balance-web/box';
import { CalendarIcon } from '@balance-web/icon';
import { theme } from '@balance-web/theme';
import { makeIdFactory, useForkedRef } from '@balance-web/utils';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { Flex } from '@balance-web/flex';
import { Divider } from '@balance-web/divider';

import type { CalendarProps } from './Calendar';
import { Calendar } from './Calendar';
import { DATE_FORMATS, dateToString, parseDateWithFormats } from './utils';
import { applyKeyboardShortcuts } from './applyKeyboardShortcuts';
import { KeyboardShotcutsLegend } from './KeyboardShotcutsLegend';

const AgGridDatePickerIdFactory = makeIdFactory('aggrid-date-picker', [
  'input',
  'clearButton',
  'popover',
  'shortcuts',
  'calendar',
]);

export type AGGridDatePickerProps = {
  /**
   * These are the props you get from AG Grid when creating a custom cell editor.
   * You're supposed to pass them directly to this component.
   */
  agGridProps: CustomCellEditorProps<any, Date | undefined>;
  /** Accessible name for the editor input. */
  ariaLabel?: string;
  keyboardShortcutLegendPosition: 'left' | 'right';
} & Pick<
  CalendarProps,
  'disabledDays' | 'fromMonth' | 'initialMonth' | 'modifiers' | 'toMonth'
>;

/**
 * We want all the changes in the textInput and calendar to be temporary.
 * The changes are committed to the consumer onChange only when user clicks a calendar date, hits Enter or Tab in the text input.
 * All other interactions (escape, blur etc) are considered a bailout and will cause the text input and calendar to revert to the consumer value.
 */
export const AGGridDatePicker = forwardRef<
  HTMLInputElement,
  AGGridDatePickerProps
>(
  (
    {
      // calendar props
      disabledDays,
      fromMonth,
      initialMonth,
      modifiers,
      toMonth,
      agGridProps,
      ariaLabel,
      keyboardShortcutLegendPosition,
      ...buttonProps
    },
    ref
  ) => {
    // @ts-ignore Santise invalid dom attributes
    if ('blur' in buttonProps && typeof buttonProps.blur === 'function') {
      delete buttonProps.blur;
    }

    const testIds = AgGridDatePickerIdFactory(agGridProps.data?.testid);

    const textInputRef = useRef<HTMLInputElement>(null);

    const textInputStyles = useInputStyles({
      size: 'small',
      shape: 'square',
      focusRing: 'bold',
    });

    /**
     * Note: agGridProps.value is not always the value in your data, it's the value ag grid
     *  stores when the cell editor is open and the value it sends to your data when you stop
     *  editing.
     * For example, when you first open the cell editor, agGridProps.value will be the same as your data.
     * If you make a change to the date, agGridProps.value will now be different from your data. When you press
     * enter or tab, the value agGridProps.value will be applied to your data and the two values will be the same again.
     */

    const [calendarDate, setCalendarDate] = useState<Date>(
      agGridProps.value ? new Date(agGridProps.value) : new Date()
    );
    const [textValue, setTextValue] = useState<string>(
      agGridProps.value
        ? dateToString((agGridProps.value as unknown) as Date)
        : ''
    );

    const composedTextInputRef = useForkedRef(textInputRef, ref);

    const focusTextInput = useCallback(
      (select?: boolean) => {
        if (textInputRef.current) {
          if (select) {
            textInputRef.current.select();
          } else {
            textInputRef.current.focus();
          }
        }
      },
      [textInputRef]
    );

    const calendarProps = {
      disabledDays,
      fromMonth,
      initialMonth,
      modifiers,
      toMonth,
    };

    const onDateChange = useCallback(
      (date: Date) => {
        // Only emit change if new value is different from the value passed in
        if (date !== agGridProps.value) {
          agGridProps.onValueChange(date);
          setTextValue(dateToString(date));
          setCalendarDate(date);
        }
      },
      [agGridProps]
    );

    const handleDayClick = useCallback(
      (day: Date, { disabled }: DayModifiers) => {
        if (disabled) {
          return;
        }
        onDateChange(day);
        agGridProps.stopEditing();
      },
      [agGridProps, onDateChange]
    );

    const commitTextValueIfValid = (value: string) => {
      const parsed = parseDateWithFormats(value, DATE_FORMATS);
      if (parsed) {
        onDateChange(parsed);
      } else {
        agGridProps.onValueChange(undefined);
      }
    };

    const onTextValueChange = (textValue: string) => {
      setTextValue(textValue);

      if (!textValue.length) {
        return;
      }

      const parsed = parseDateWithFormats(textValue, DATE_FORMATS);
      if (parsed) {
        setCalendarDate(parsed);
        return;
      }

      const hasSeparator = textValue.includes('/') || textValue.includes('-');
      if (!hasSeparator) {
        return;
      }

      const [currentDay, currentMonth, currentYear] = dateToString(
        calendarDate
      ).split('/');
      const separator = textValue.includes('/') ? '/' : '-';
      const parts = textValue.split(separator);

      const day = parts[0]?.padStart(2, '0') || currentDay;
      const month = parts[1]?.padStart(2, '0') || currentMonth;
      const year = parts[2] || currentYear;

      const partialDateStr = `${day}/${month}/${
        year.length === 2 ? year : year.slice(0, 4)
      }`;
      const partialDate = parseDateWithFormats(partialDateStr, DATE_FORMATS);
      if (partialDate) {
        setCalendarDate(partialDate);
      }
    };

    useEffect(function focusInputAndOpenCalendarOnMount() {
      focusTextInput(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Box
        css={{
          height: agGridProps.node.rowHeight || undefined,
        }}
        data-testid={testIds.base(agGridProps.node.id)}
      >
        <Flex height="100%">
          <input
            ref={composedTextInputRef}
            aria-label={
              ariaLabel ??
              agGridProps.colDef.headerName ??
              agGridProps.column.getColId()
            }
            autoComplete="off"
            css={[
              textInputStyles,
              {
                borderRadius: theme.radii.xsmall,
                width: agGridProps.column.getActualWidth(),
                height: '100%',
              },
            ]}
            data-testid={testIds.input(agGridProps.node.id)}
            placeholder="dd/mm/yyyy"
            value={textValue}
            onBlur={() => {
              commitTextValueIfValid(textValue);
            }}
            onChange={(e) => {
              onTextValueChange(e.target.value);
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Tab' || e.key === 'Enter') {
                commitTextValueIfValid(textValue);
                agGridProps.stopEditing();
                return;
              }

              applyKeyboardShortcuts({
                e,
                onDateChange,
                baseDate: calendarDate,
              });
            }}
          />

          <div
            css={{
              position: 'relative',
              right: `calc(16px + ${theme.spacing.medium})`,
              top: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CalendarIcon color="textTertiary" size="small" />
          </div>
        </Flex>

        <div
          css={{
            position: 'relative',
            background: theme.semantic.colour.surface.neutral.background,
            borderRadius: theme.radii.small,
            boxShadow: theme.shadow.medium,
          }}
        >
          <Flex>
            <Calendar
              data={{ testid: testIds.calendar(agGridProps.node.id) }}
              month={calendarDate}
              selectedDays={calendarDate}
              onDayClick={handleDayClick}
              {...calendarProps}
            />
            {keyboardShortcutLegendPosition === 'right' ? (
              <Divider vertical />
            ) : null}
            <KeyboardShotcutsLegend
              data={{ testid: testIds.shortcuts(agGridProps.node.id) }}
            />
          </Flex>
        </div>
      </Box>
    );
  }
);
